// <SkipLink> + <SkipLinks> — first DOM node before logo per Google convention.
//
// Closes A11y QA §A.3 (skip-link DOM order).
// Closes TP-11 (the visually-hidden helper moves into the bridge).
//
// The link is visually hidden at rest; on `:focus-visible` it becomes
// visible at the top-left of the viewport per the global stylesheet
// hooked in primitives.tsx (`[data-geosoft-skip-link]`).

import type { CSSProperties } from 'react';
import { tokens } from '../tokens';

export interface SkipLinkProps {
  href: string;
  children: React.ReactNode;
  'data-testid'?: string;
}

export function SkipLink({ href, children, 'data-testid': testId }: SkipLinkProps) {
  const style: CSSProperties = {
    // Visible-on-focus styles when the global :focus-visible rule fires.
    // These inline styles only apply once the global toggles `clip-path:none`.
    top: tokens.space[2],
    left: tokens.space[2],
    zIndex: tokens.zIndex.toast,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.body.size,
    lineHeight: tokens.font.role.body.lineHeight,
    background: tokens.color.accent.primary,
    color: tokens.color.accent.on,
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    borderRadius: tokens.radius.input,
    textDecoration: 'none',
  };
  return (
    <a
      data-geosoft-skip-link
      data-testid={testId}
      href={href}
      style={style}
    >
      {children}
    </a>
  );
}

export interface SkipLinksProps {
  /** Skip-link targets, in order. Default: map / layers / copilot. */
  links?: ReadonlyArray<{ href: string; label: string }>;
  'data-testid'?: string;
}

const DEFAULT_LINKS: ReadonlyArray<{ href: string; label: string }> = [
  { href: '#map', label: 'Skip to map' },
  { href: '#layers', label: 'Skip to layers' },
  { href: '#copilot', label: 'Skip to copilot' },
];

export function SkipLinks({ links = DEFAULT_LINKS, 'data-testid': testId }: SkipLinksProps) {
  return (
    <nav
      aria-label="Skip links"
      data-testid={testId}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: tokens.zIndex.toast,
        display: 'flex',
        gap: tokens.space[1],
      }}
    >
      {links.map((link) => (
        <SkipLink key={link.href} href={link.href}>
          {link.label}
        </SkipLink>
      ))}
    </nav>
  );
}
